import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { firstValueFrom, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthSessionService } from './auth-session.service';
import { FirebaseAuthService } from './firebase-auth.service';

export const DEV_LOGIN_CODE_PARAM = 'dev_login_code';

/** Longest the startup waits for one request of the developer login. */
const STARTUP_TIMEOUT_MS = 10_000;

interface DevLoginExchangeResponse {
  firebaseCustomToken: string;
}

/**
 * Developer login as a Stella person: the bpp-auth dashboard opens the portal with a one-time
 * `?dev_login_code=` (single-use, 5 min). Runs as an app initializer, i.e. before the router's first
 * navigation, so `authGuard` never copies the code into `returnUrl`.
 *
 * The code leaves the address bar and the history entry before anything else happens. Someone
 * already signed in is logged out, whatever the code turns out to be; then the code is exchanged for
 * a Firebase custom token and the target person signed in — routing then follows the usual
 * `currentUser$` → `AuthSessionService` → guard path. A failure is only logged, without the code,
 * and the portal starts as if there had been none.
 */
export async function devLoginInitializer(): Promise<void> {
  const firebaseAuth = inject(FirebaseAuthService);
  const injector = inject(Injector);
  // Straight to the backend, past the interceptors: the exchange is anonymous
  const http = new HttpClient(inject(HttpBackend));

  const url = new URL(window.location.href);
  if (!url.searchParams.has(DEV_LOGIN_CODE_PARAM)) {
    return;
  }
  const code = url.searchParams.get(DEV_LOGIN_CODE_PARAM);
  url.searchParams.delete(DEV_LOGIN_CODE_PARAM);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  if (!code) {
    return;
  }

  try {
    if (await firebaseAuth.getCurrentUser()) {
      await logoutCurrentPersonAsync(injector);
    }

    const response = await firstValueFrom(
      http
        .post<DevLoginExchangeResponse>(`${environment.bppAuth}/api/auth-dashboard/dev-login/exchange`, { code })
        .pipe(timeout(STARTUP_TIMEOUT_MS))
    );
    await firebaseAuth.signInWithCustomTokenAsync(response.firebaseCustomToken);
  } catch (error) {
    const status = error instanceof HttpErrorResponse ? error.status : null;
    console.error('Developer login failed:', status !== null ? `HTTP ${status}` : String(error));
  }
}

async function logoutCurrentPersonAsync(injector: Injector): Promise<void> {
  // The logout switches browser push off for the person the session knows; at startup nobody has loaded them yet
  await firstValueFrom(
    injector.get(AuthSessionService).awaitDestination().pipe(timeout({ first: STARTUP_TIMEOUT_MS, with: () => of(null) }))
  );

  // Lazy like in DevLoginExpiryService: LogoutService pulls every person-scoped state service
  const { LogoutService } = await import('./logout.service');
  await injector.get(LogoutService).logoutAsync(false);
}
